import { useState } from 'react';
import { UMIcon } from './UMIcon';
import { UMR } from './referenceTokens';

export type UMRailItemId =
  | 'layers'
  | 'geometries'
  | 'measures'
  | 'filters'
  | 'selection'
  | 'export'
  | 'import'
  | 'admin'
  | 'contact'
  | 'logout'
  | (string & {});

export interface UMRailItem {
  id: UMRailItemId;
  icon: string;
  label: string;
  /** Rail group: items are separated by a 1px divider between groups. */
  group: 'tools' | 'data' | 'io' | 'footer';
  disabled?: boolean;
}

/** Exact production rail (order, icons, labels, groups, disabled state). */
export const UM_RAIL_ITEMS: UMRailItem[] = [
  { id: 'layers', icon: 'layers', label: 'Couches', group: 'tools' },
  { id: 'geometries', icon: 'shape_line', label: 'Géométries', group: 'tools' },
  { id: 'measures', icon: 'straighten', label: 'Mesures', group: 'tools' },
  { id: 'filters', icon: 'filter_list', label: 'Filtres', group: 'data' },
  { id: 'selection', icon: 'folder_open', label: 'Sélection', group: 'data', disabled: true },
  { id: 'export', icon: 'download', label: 'Export', group: 'io' },
  { id: 'import', icon: 'upload', label: 'Import', group: 'io' },
  { id: 'admin', icon: 'settings', label: 'Admin', group: 'footer' },
  { id: 'contact', icon: 'mail', label: 'Contact', group: 'footer' },
  { id: 'logout', icon: 'logout', label: 'Déconnexion', group: 'footer' },
];

interface UMRailProps {
  items?: UMRailItem[];
  activeId?: UMRailItemId | null;
  onSelect?: (id: UMRailItemId) => void;
}

function Divider() {
  return <div style={{ margin: '12px 0', borderTop: `1px solid ${UMR.borderSubtle}` }} />;
}

function RailButton({ item, active, onSelect }: { item: UMRailItem; active: boolean; onSelect?: (id: UMRailItemId) => void }) {
  const [hover, setHover] = useState(false);
  const color = item.disabled ? UMR.textDisabled : UMR.text;
  return (
    <button
      type="button"
      disabled={item.disabled}
      aria-pressed={active}
      onClick={() => onSelect?.(item.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        height: UMR.size.railItemHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '8px 0',
        border: 'none',
        // Production shows no persistent active state, only hover (bg-blue-200).
        background: !item.disabled && hover ? UMR.railHover : 'transparent',
        color,
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        fontFamily: UMR.font,
      }}
    >
      <UMIcon name={item.icon} size={UMR.size.railIcon} color={color} />
      <span style={{ fontSize: UMR.size.railLabel, lineHeight: '16px' }}>{item.label}</span>
    </button>
  );
}

/** Left icon rail of UrbanMap (80px, Material Symbols, 3 groups + footer). */
export function UMRail({ items = UM_RAIL_ITEMS, activeId = null, onSelect }: UMRailProps) {
  const main = items.filter((i) => i.group !== 'footer');
  const footer = items.filter((i) => i.group === 'footer');
  return (
    <nav
      aria-label="Navigation UrbanMap"
      style={{
        width: UMR.size.railWidth,
        minWidth: UMR.size.railWidth,
        height: '100%',
        borderRight: `1px solid ${UMR.border}`,
        background: UMR.white,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        fontFamily: UMR.font,
        zIndex: 30,
      }}
    >
      <div style={{ padding: '16px 0' }}>
        <div
          style={{
            width: UMR.size.logo,
            height: UMR.size.logo,
            margin: '0 auto',
            background: UMR.logoBg,
            color: UMR.logoText,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          UM
        </div>
      </div>
      {main.map((item, index) => (
        <div key={item.id}>
          {index > 0 && main[index - 1].group !== item.group && <Divider />}
          <RailButton item={item} active={activeId === item.id} onSelect={onSelect} />
        </div>
      ))}
      {footer.length > 0 && (
        <div style={{ marginTop: 'auto' }}>
          <Divider />
          {footer.map((item) => (
            <RailButton key={item.id} item={item} active={activeId === item.id} onSelect={onSelect} />
          ))}
        </div>
      )}
    </nav>
  );
}
